import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useFleetPermissions } from '@/hooks/use-fleet-permissions';
import {
  useUnreadMarkingNotifications,
  useMarkingNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '@/hooks/use-marking-notifications';
import { Activity, Bell, Users, Fuel, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { MetricCard } from '@/components/fleet/MetricCard';
import { MarkingNotificationsFeed } from '@/components/fleet/MarkingNotificationsFeed';
import { MarkingsByHourChart } from '@/components/fleet/MarkingsByHourChart';
import { MarkingTypeDistributionChart } from '@/components/fleet/MarkingTypeDistributionChart';

export default function FleetOpsManagerDashboard() {
  const { activeCompany } = useAuth();
  const { isFleetOpsManager } = useFleetPermissions();
  const navigate = useNavigate();

  const { data: unreadNotifications = [] } = useUnreadMarkingNotifications(activeCompany?.id);
  const { data: allNotifications = [] } = useMarkingNotifications(activeCompany?.id);
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead(activeCompany?.id);

  // Check permissions
  if (!isFleetOpsManager) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert variant="destructive">
          <AlertTitle>Acesso Negado</AlertTitle>
          <AlertDescription>
            Você não tem permissão para acessar esta área. Esta área é exclusiva para gerentes de operações.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Metrics
  const today = new Date().toISOString().split('T')[0];
  const todayNotifications = allNotifications.filter(n => 
    n.created_at.startsWith(today)
  );
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
  const recentNotifications = allNotifications.filter(n => 
    n.created_at >= twoHoursAgo
  );
  const uniqueDriversToday = new Set(todayNotifications.map(n => n.driver_id)).size;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Operações</h1>
          <p className="text-muted-foreground">
            Monitoramento em tempo real das marcações dos motoristas
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => markAllAsRead.mutate()}
          disabled={unreadNotifications.length === 0}
        >
          <CheckCheck className="mr-2 h-4 w-4" />
          Marcar todas como lidas
        </Button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Marcações Hoje"
          value={todayNotifications.length}
          icon={<Activity className="h-6 w-6" />}
          trend={`${recentNotifications.length} nas últimas 2h`}
        />
        <MetricCard
          title="Não Lidas"
          value={unreadNotifications.length}
          icon={<Bell className="h-6 w-6" />}
          variant={unreadNotifications.length > 0 ? "warning" : "default"}
        />
        <MetricCard
          title="Motoristas Ativos"
          value={uniqueDriversToday}
          icon={<Users className="h-6 w-6" />}
        />
        <MetricCard
          title="Abastecimentos"
          value={todayNotifications.filter(n => n.marking_type === 'fuel').length}
          icon={<Fuel className="h-6 w-6" />}
        />
      </div>

      {/* Notifications Feed */}
      <Card>
        <CardHeader>
          <CardTitle>Feed de Marcações em Tempo Real</CardTitle>
        </CardHeader>
        <CardContent>
          <MarkingNotificationsFeed
            notifications={allNotifications}
            unreadIds={unreadNotifications.map(n => n.id)}
            onMarkAsRead={(id) => markAsRead.mutate(id)}
          />
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Marcações por Hora (Hoje)</CardTitle>
          </CardHeader>
          <CardContent>
            <MarkingsByHourChart data={todayNotifications} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Tipo (Hoje)</CardTitle>
          </CardHeader>
          <CardContent>
            <MarkingTypeDistributionChart data={todayNotifications} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
